import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {
  Button,
  StatesButton,
  StatesButtonStates,
  Text,
  TextFieldTheme,
  TextTypography,
  ThreeDotsLoader,
} from 'wix-ui-tpa/cssVars'

import {PhoneInput} from '../../../../components-shared/PhoneInput'
import {FormError} from '../../../../components-shared/FormError'
import {reservationConfirmationDataHooks} from '../../Widget/constants'
import {Modal} from '../../../../components-shared/Modal'
import {classes as modalClasses} from '../../../../components-shared/Modal/styles.st.css'
import {CancellationFeeNotice} from '../CancellationFeeNotice'
import {CancellationPolicyDialog} from '../CancellationPolicyDialog'

import {st, classes} from './styles.st.css'
import {useHooks} from './useHooks'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const CancelModal: FC<Props> = ({isOpen, onClose}) => {
  const [isPolicyRequested, setIsPolicyRequested] = useState(false)
  const isPolicyOpenRef = useRef(false)
  const handleDismissRef = useRef<(origin: 'no_button' | 'close_button') => void>()
  const openPolicy = useCallback(() => setIsPolicyRequested(true), [])
  const closePolicy = useCallback(() => setIsPolicyRequested(false), [])
  const {
    t,
    isMobile,
    phone,
    country,
    phoneErrorText,
    isCancelReservationLoading,
    cancelReservation,
    handlePhoneChange,
    errorMessage,
    handleDismiss,
    feeApplies,
    isFeePreviewPending,
    legacyCancellationFeePreview,
    feeWarning,
  } = useHooks(isOpen, onClose)
  handleDismissRef.current = handleDismiss

  const dismissCancelModal = useCallback((origin: 'no_button' | 'close_button') => {
    if (isPolicyOpenRef.current) {
      return
    }

    handleDismissRef.current?.(origin)
  }, [])
  const closeCancelModal = useCallback(
    () => dismissCancelModal('close_button'),
    [dismissCancelModal],
  )
  const rejectCancellation = useCallback(
    () => dismissCancelModal('no_button'),
    [dismissCancelModal],
  )
  const phoneInstruction = t(
    'uou-reservations.reservation-confirmation.modal-cancel-phone.instruction',
  )
  const isConfirmDisabled = isCancelReservationLoading || isFeePreviewPending
  const isPolicyOpen = isPolicyRequested && feeApplies && Boolean(feeWarning.guestPolicy)

  isPolicyOpenRef.current = isPolicyOpen

  useEffect(() => {
    if (!isOpen || !feeApplies || !feeWarning.guestPolicy) {
      setIsPolicyRequested(false)
    }
  }, [feeApplies, feeWarning.guestPolicy, isOpen])

  const legacyFeeNotice = (() => {
    if (feeApplies) {
      return null
    }

    if (legacyCancellationFeePreview?.type === 'FREE_CANCELLATION') {
      return t('uou-reservations.reservation-confirmation.cancel-fee-free', {
        window: legacyCancellationFeePreview.windowDescription,
      })
    }

    if (legacyCancellationFeePreview?.type === 'FEE_APPLIES' && !isFeePreviewPending) {
      return t('uou-reservations.reservation-confirmation.cancel-fee-applies', {
        amount: legacyCancellationFeePreview.totalAmount,
        currency: legacyCancellationFeePreview.currency,
      })
    }

    return null
  })()

  return (
    <Modal
      isOpen={isOpen}
      manualFocus={isPolicyOpen}
      contentClassName={classes.cancelDialog}
      onClose={closeCancelModal}
      className={st(classes.root, {
        mobile: isMobile,
      })}
    >
      <Text typography={TextTypography.smallTitle} className={classes.title} tagName="h2">
        {t('uou-reservations.reservation-confirmation.modal-cancel-title')}
      </Text>
      {feeApplies ? (
        <CancellationFeeNotice
          t={t}
          feeWarning={feeWarning}
          className={classes.modalFeeNotice}
          onViewPolicy={openPolicy}
        />
      ) : null}
      <CancellationPolicyDialog
        t={t}
        isOpen={isPolicyOpen}
        policy={feeWarning.guestPolicy}
        onClose={closePolicy}
      />
      <div className={st(classes.bodyText, classes.cancelDialogTextWrapper)}>
        <Text typography={TextTypography.listText} tagName="p" className={classes.bodyText}>
          {feeApplies
            ? phoneInstruction
            : t('uou-reservations.reservation-confirmation.modal-cancel-text')}
        </Text>
        {legacyFeeNotice ? (
          <Text typography={TextTypography.listText} tagName="p" className={classes.bodyText}>
            {legacyFeeNotice}
          </Text>
        ) : null}
      </div>
      <div
        className={st(
          classes.cancelDialogPhoneWrapper,
          phoneErrorText || errorMessage ? classes.cancelDialogPhoneWrapperWithError : undefined,
        )}
      >
        <PhoneInput
          label={t('uou-reservations.reservation-confirmation.modal-cancel-phone.label')}
          value={phone}
          onChange={handlePhoneChange}
          textFieldTheme={TextFieldTheme.Box}
          wrapperClassName={classes.phoneWrapper}
          labelClassName={st(classes.bodyText, classes.phoneInputLabel)}
          className={classes.phoneInputWrapper}
          disabled={isCancelReservationLoading}
          invalid={!!phoneErrorText}
          defaultCountry={country}
          dataHook={reservationConfirmationDataHooks.cancelReservationPhoneInput()}
        />
        {phoneErrorText && (
          <FormError text={phoneErrorText} className={st(classes.bodyText, classes.error)} />
        )}
        {errorMessage && (
          <FormError text={errorMessage} className={st(classes.bodyText, classes.error)} />
        )}
      </div>
      <div className={classes.cancelDialogButtonsWrapper}>
        <Button className={modalClasses.invertedButton} onClick={rejectCancellation}>
          {t('uou-reservations.reservation-confirmation.modal-cancel-no')}
        </Button>
        <StatesButton
          type="button"
          onClick={cancelReservation}
          state={
            isCancelReservationLoading ? StatesButtonStates.IN_PROGRESS : StatesButtonStates.IDLE
          }
          idleContent={t('uou-reservations.reservation-confirmation.modal-cancel-yes')}
          inProgressContent={<ThreeDotsLoader className={classes.loader} />}
          data-hook={reservationConfirmationDataHooks.cancelReservationButton()}
          disabled={isConfirmDisabled}
          className={classes.cancelReservationButton}
        />
      </div>
    </Modal>
  )
}

export default CancelModal
